/** Recorded boat engines and hull water, with procedural weather and impacts. */
import type { VesselKind } from '../entities/Tug.ts'
import { BoatAudio } from './BoatAudio.ts'

export class EngineSound {
  private context: AudioContext | null = null
  private master: GainNode | null = null

  private boatAudio: BoatAudio | null = null
  private vessel: VesselKind = 'tug'
  private paused = false

  private rainGain: GainNode | null = null
  private noiseBuffer: AudioBuffer | null = null
  /** Recorded thunder, decoded on unlock. Strikes slice random windows of it. */
  private thunderBuffer: AudioBuffer | null = null

  private started = false
  private muted = false

  get isMuted(): boolean {
    return this.muted
  }

  setVessel(kind: VesselKind): void {
    this.vessel = kind
    this.boatAudio?.setVessel(kind)
  }

  /** Mute silences the whole mix but keeps the engine running. */
  setMuted(muted: boolean): void {
    this.muted = muted
    if (this.master && this.context) {
      this.master.gain.cancelScheduledValues(this.context.currentTime)
      this.master.gain.setTargetAtTime(muted ? 0 : 0.55, this.context.currentTime, 0.05)
    }
  }

  /** Halt all audio processing — used while the game is paused on the menu. */
  suspend(): void {
    this.paused = true
    void this.context?.suspend()
  }

  /** Resume after a pause (stays silent if the player has muted). */
  resume(): void {
    this.paused = false
    void this.context?.resume()
  }

  /** Call from a user-gesture handler. Safe to call repeatedly. */
  unlock(): void {
    if (this.paused || document.hidden) return
    if (this.started) {
      // Some browsers re-suspend on tab switches; nudge it back.
      void this.context?.resume()
      return
    }
    const context = new AudioContext()
    this.context = context
    this.started = true

    this.master = context.createGain()
    this.master.gain.value = 0
    // Leave headroom for thunder/impacts without clipping small speakers.
    const limiter = context.createDynamicsCompressor()
    limiter.threshold.value = -8
    limiter.knee.value = 6
    limiter.ratio.value = 8
    limiter.attack.value = .005
    limiter.release.value = .2
    this.master.connect(limiter)
    limiter.connect(context.destination)

    this.boatAudio = new BoatAudio(context, this.master)
    this.boatAudio.setVessel(this.vessel)

    // --- shared noise source ---------------------------------------------

    const noiseBuffer = context.createBuffer(1, context.sampleRate * 2, context.sampleRate)
    const samples = noiseBuffer.getChannelData(0)
    for (let i = 0; i < samples.length; i++) samples[i] = Math.random() * 2 - 1
    this.noiseBuffer = noiseBuffer

    const noise = context.createBufferSource()
    noise.buffer = noiseBuffer
    noise.loop = true
    noise.start()

    // --- rain -------------------------------------------------------------
    // Broadband noise, high-passed into hiss. Silent until a storm rolls in.

    const rainFilter = context.createBiquadFilter()
    rainFilter.type = 'highpass'
    rainFilter.frequency.value = 1600
    rainFilter.Q.value = 0.3

    this.rainGain = context.createGain()
    this.rainGain.gain.value = 0
    noise.connect(rainFilter)
    rainFilter.connect(this.rainGain)
    this.rainGain.connect(this.master)

    // Fade the whole mix in so unlocking mid-game doesn't pop.
    this.master.gain.linearRampToValueAtTime(this.muted ? 0 : 0.55, context.currentTime + 1.2)

    // Recorded thunder, fetched lazily — the game is fully playable before
    // (and without) it; strikes fall back to the synthesised clap.
    fetch('/audio/thunder.mp3')
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.arrayBuffer()
      })
      .then((data) => context.decodeAudioData(data))
      .then((buffer) => {
        this.thunderBuffer = buffer
      })
      .catch((error) => console.warn('Thunder sample failed to load, using synth fallback:', error))
  }

  /** Rain intensity, 0..1. Tracks the storm factor. */
  setRain(strength: number): void {
    if (!this.context || this.context.state !== 'running') return
    this.rainGain!.gain.setTargetAtTime(strength * 0.16, this.context.currentTime, 0.8)
  }

  /**
   * One thunderclap. Preferred source is the recorded storm track: each strike
   * plays a random 8-second window at a slightly randomised rate, so one file
   * yields endless distinct claps. Until (or unless) that decodes, a
   * synthesised crack-into-rumble stands in.
   */
  thunder(intensity: number): void {
    if (!this.context || this.context.state !== 'running') return
    const context = this.context
    const now = context.currentTime

    if (this.thunderBuffer) {
      const sample = context.createBufferSource()
      sample.buffer = this.thunderBuffer
      sample.playbackRate.value = 0.92 + Math.random() * 0.16

      const window = Math.min(8, this.thunderBuffer.duration)
      const offset = Math.random() * Math.max(0, this.thunderBuffer.duration - window)

      const sampleGain = context.createGain()
      sampleGain.gain.setValueAtTime(0, now)
      // Thunder is the loudest thing in the storm — it should make you jump.
      sampleGain.gain.linearRampToValueAtTime(2.6 * intensity, now + 0.06)
      sampleGain.gain.setValueAtTime(2.6 * intensity, now + window - 2)
      sampleGain.gain.linearRampToValueAtTime(0, now + window)

      sample.connect(sampleGain)
      sampleGain.connect(this.master!)
      sample.start(now, offset, window)
      return
    }

    if (!this.noiseBuffer) return
    const source = context.createBufferSource()
    source.buffer = this.noiseBuffer
    source.loop = true

    const filter = context.createBiquadFilter()
    filter.type = 'lowpass'
    filter.Q.value = 0.9
    filter.frequency.setValueAtTime(950, now)
    filter.frequency.exponentialRampToValueAtTime(70, now + 0.5)

    const gain = context.createGain()
    gain.gain.setValueAtTime(0, now)
    gain.gain.linearRampToValueAtTime(2.2 * intensity, now + 0.04)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 2.8 + intensity * 1.5)

    source.connect(filter)
    filter.connect(gain)
    gain.connect(this.master!)
    source.start(now)
    source.stop(now + 5)
  }

  /**
   * Running aground: a heavy hull striking the bottom. Two layers — a low sine
   * thud that pitches down as the hull decelerates into the sand, and a
   * lowpassed noise grind that scrapes and fades as she settles. Entirely
   * synthesised, no sample needed.
   */
  crash(): void {
    if (!this.context || this.context.state !== 'running' || !this.noiseBuffer) return
    const context = this.context
    const now = context.currentTime

    // --- the thud ---------------------------------------------------------
    const thud = context.createOscillator()
    thud.type = 'sine'
    thud.frequency.setValueAtTime(120, now)
    thud.frequency.exponentialRampToValueAtTime(38, now + 0.35)

    const thudGain = context.createGain()
    thudGain.gain.setValueAtTime(0, now)
    thudGain.gain.linearRampToValueAtTime(1.6, now + 0.02)
    thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.6)

    thud.connect(thudGain)
    thudGain.connect(this.master!)
    thud.start(now)
    thud.stop(now + 0.7)

    // --- the grind --------------------------------------------------------
    const grind = context.createBufferSource()
    grind.buffer = this.noiseBuffer
    grind.loop = true

    const grindFilter = context.createBiquadFilter()
    grindFilter.type = 'lowpass'
    grindFilter.Q.value = 1.4
    grindFilter.frequency.setValueAtTime(700, now)
    grindFilter.frequency.exponentialRampToValueAtTime(160, now + 0.8)

    const grindGain = context.createGain()
    grindGain.gain.setValueAtTime(0, now)
    grindGain.gain.linearRampToValueAtTime(0.9, now + 0.03)
    grindGain.gain.exponentialRampToValueAtTime(0.001, now + 1.0)

    grind.connect(grindFilter)
    grindFilter.connect(grindGain)
    grindGain.connect(this.master!)
    grind.start(now)
    grind.stop(now + 1.2)
  }

  /**
   * Track the engine state. `load` is how hard the engine is working (tow
   * tension leaning on it), 0..1; `speed` is hull speed in m/s for the water
   * layer.
   */
  update(throttle: number, load: number, speed: number): void {
    if (!this.context || this.context.state !== 'running') return
    this.boatAudio?.update(throttle, load, speed)
  }
}
